import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { appDate } from '@/utils/appDateFormat';
import { useSelector } from 'react-redux';
import { FolderKanban } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from '../../../../components/ui/Dialog';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../../components/ui/Table';
import Button from '../../../../components/ui/Button';
import Input from '../../../../components/ui/Input';
import { Spinner } from '../../../../components/ui/Spinner';
import { PageLayout } from '../../../../components/ui/PageLayout';
import { ProjectInput, ProjectView } from '../../../../models/projectManagement';
import { createProject, getProjects, updateProject, getProject } from '../../../../api/ProjectManagement/project';

interface RootState {
  user: { company: any };
}

export default function Projects() {
  const { t } = useTranslation();
  const company = useSelector((state: RootState) => state.user.company);

  const [projects, setProjects] = useState<ProjectView[]>([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [input, setInput] = useState<ProjectInput>({} as ProjectInput);
  const [editInput, setEditInput] = useState<ProjectInput>({} as ProjectInput);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getProjects();
      setProjects(response.payload);
    } catch (ex) {
      console.log(ex);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenAdd = () => {
    setOpenAdd((prev) => !prev);
  };

  const handleOpenEdit = async (id: number) => {
    setLoading(true);
    try {
      const response = await getProject(id);
      setEditInput(response.payload);
      setOpenEdit(true);
    } catch (ex) {
      console.log(ex);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await createProject(input);
      setOpenAdd(false);
      fetchData();
    } catch (ex) {
      console.log(ex);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    setLoading(true);
    try {
      await updateProject(editInput);
      setOpenEdit(false);
      fetchData();
    } catch (ex) {
      console.log(ex);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout
      title={t('projects:Projects')}
      icon={FolderKanban}
      accentColor="slate"
      headerActions={(
        <Button variant="outline" iconName="Plus" onClick={handleOpenAdd}>
          {t('projects:Create Project')}
        </Button>
      )}
    >
      {/* Table */}
      <div className="rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common:Name')}</TableHead>
              <TableHead>{t('projects:Description')}</TableHead>
              <TableHead>{t('common:Start Date')}</TableHead>
              {/* <TableHead>{t('projects:Completion Date')}</TableHead> */}
              <TableHead>{t('common:Actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-6">
                  <Spinner size="md" />
                </TableCell>
              </TableRow>
            )}
            {!loading && projects.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                  {t('common:No data')}
                </TableCell>
              </TableRow>
            )}
            {!loading && projects.map((project) => (
              <TableRow key={project.id}>
                <TableCell>{project.title}</TableCell>
                <TableCell>{project.description}</TableCell>
                <TableCell>{appDate(project.startDate)}</TableCell>
                {/* <TableCell>{project.completionDate ? appDate(project.completionDate) : ''}</TableCell> */}
                <TableCell>
                  <Button variant="outline" size="sm" onClick={() => handleOpenEdit(project.id)}>
                    {t('common:Edit')}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add Dialog */}
      <Dialog open={openAdd} onOpenChange={setOpenAdd}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t('projects:Create Project')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            <Input
              autoFocus
              label={t('projects:Title')}
              value={input.title || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput({ ...input, title: e.target.value })}
            />
            <Input
              label={t('projects:Description')}
              value={input.description || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInput({ ...input, description: e.target.value })}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={handleOpenAdd}>
              {t('common:Cancel')}
            </Button>
            <Button onClick={handleSubmit} loading={loading}>
              {t('projects:Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openEdit} onOpenChange={setOpenEdit}>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>{t('projects:Edit Project')}</DialogTitle>
          </DialogHeader>
          <DialogBody className="flex flex-col gap-4">
            <Input
              autoFocus
              label={t('common:Name')}
              value={editInput.title || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditInput({ ...editInput, title: e.target.value })}
            />
            <Input
              label={t('projects:Description')}
              value={editInput.description || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditInput({ ...editInput, description: e.target.value })}
            />
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenEdit(false)}>
              {t('common:Cancel')}
            </Button>
            <Button onClick={handleUpdate} loading={loading}>
              {t('common:Update')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
}
